// src/modules/app-views/goodsReceipt/pages/Init/modals/NfeImportModal/index.jsx
// *******************************************************************************
// NFE IMPORT MODAL
// *******************************************************************************
// 2-step GenericModal for importing fiscal documents (XML, PDF, image).
// Step 1: File upload (drag & drop via Form.Item)
// Step 2: Processing result / duplicate handling
// Uses imperative showNfeImportModal() pattern.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useRef } from 'react';
import { FileAddOutlined } from '@ant-design/icons';
import GenericModal, { openModal } from 'components/core/GenericModal';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import step1Upload from './Step1';
import step2Processing from './Step2';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfeImportModal = ({ show, setShow, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [importState, setImportState] = useState({
    status: 'idle', // idle | loading | success | error | duplicate
    data: null,
    error: null,
  });
  const fileRef = useRef(null);
  const hasStartedRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleImport = useCallback(async (overwrite = false) => {
    const file = fileRef.current;
    if (!file) {
      setImportState({
        status: 'error',
        data: null,
        error: 'No file selected',
      });
      return;
    }

    setImportState({ status: 'loading', data: null, error: null });

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (overwrite) {
        formData.append('overwrite', 'true');
      }

      const url = API.GOODS_RECEIPTS.IMPORT;

      const response = await axiosService.post({
        url,
        data: formData,
      });

      // axiosService.post returns [doc, rawResult] with returnAsObject
      const result = Array.isArray(response) ? response[0] : response;

      if (!result) {
        setImportState({
          status: 'error',
          data: null,
          error: translateX('unexpected_error'),
        });
        return;
      }

      // Check for duplicate
      if (result.duplicate) {
        setImportState({
          status: 'duplicate',
          data: result,
          error: null,
        });
        return;
      }

      // Refresh parameters before showing success — new units may have been created
      // during import; parameters must be current before user navigates to the document.
      try {
        const { useAppStore } = await import('stores/app');
        const refreshFn = useAppStore.getState().refreshParameters;
        if (typeof refreshFn === 'function') await refreshFn();
      } catch (_e) {
        // non-fatal — parameters will be stale until next load
      }

      setImportState({
        status: 'success',
        data: result,
        error: null,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[NfeImportModal] Import error:', err);
      setImportState({
        status: 'error',
        data: null,
        error: err?.message || translateX('unexpected_error'),
      });
    }
  }, []);

  const handleOverwrite = useCallback(() => {
    handleImport(true);
  }, [handleImport]);

  const handleFinish = useCallback(({ form }) => {
    // If success, call onSuccess callback (refresh list)
    if (importState.status === 'success') {
      if (typeof onSuccess === 'function') onSuccess();
      return; // Close modal
    }

    // If duplicate draft, trigger overwrite
    if (importState.data?.duplicate && importState.data?.canOverwrite) {
      handleOverwrite();
      return false; // Keep modal open
    }

    return; // Close modal
  }, [importState, onSuccess, handleOverwrite]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const isLoading = importState.status === 'loading';
  const isDuplicateConfirmed = importState.data?.duplicate && !importState.data?.canOverwrite;

  const steps = [
    step1Upload(),
    {
      ...step2Processing({ importState }),
      onEnter: ({ form }) => {
        // Guard: prevent re-triggering on state changes
        if (hasStartedRef.current) return;
        hasStartedRef.current = true;

        // Extract file from Form.Item fileList
        const fileList = form.getFieldValue('file');
        const uploadedFile = fileList?.[0]?.originFileObj || fileList?.[0];

        if (uploadedFile instanceof File) {
          fileRef.current = uploadedFile;
        }

        // Start import
        handleImport(false);
      },
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={translateX('import_fiscal_document')}
      icon={<FileAddOutlined />}
      steps={steps}
      onFinish={handleFinish}
      width={560}
      maskClosable={!isLoading}
      closable={!isLoading}
      disableBack={isLoading}
      confirmDisabled={isLoading || isDuplicateConfirmed}
      afterClose={() => {
        setImportState({ status: 'idle', data: null, error: null });
        fileRef.current = null;
        hasStartedRef.current = false;
      }}
    />
  );
};

// *******************************************************************************
// IMPERATIVE OPENER
// *******************************************************************************

function showNfeImportModal({ onSuccess } = {}) {
  const WrappedModal = (props) => (
    <NfeImportModal {...props} onSuccess={onSuccess} />
  );
  openModal(WrappedModal);
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showNfeImportModal;
